import type { GridPoint } from '../../core/types';
import type { WorldMap } from '../../world/WorldMap';
import { buildingDef, type BuildingDefinition } from './definitions';
import { distanceToFootprint, footprintTiles } from './footprint';
import type { OccupancyGrid } from './OccupancyGrid';

export type PlacementFailure = 'out_of_bounds' | 'terrain' | 'occupied' | 'spawn_zone' | 'outside_territory' | 'requires_nearby' | 'insufficient_resources' | 'units_in_way';

export interface TileCheck {
  readonly x: number;
  readonly y: number;
  readonly ok: boolean;
  readonly reason: PlacementFailure | null;
}

export interface PlacementResult {
  readonly ok: boolean;
  readonly definition: BuildingDefinition;
  readonly origin: GridPoint;
  /** One entry per footprint tile, for the preview. */
  readonly tiles: readonly TileCheck[];
  /** Distinct failures, in the order checked. */
  readonly failures: readonly PlacementFailure[];
  /** Player-facing explanation (empty when ok). */
  readonly message: string;
}

export interface PlacementContext {
  readonly map: WorldMap;
  readonly occupancy: OccupancyGrid;
  /** Name lookup for "occupied by …" messages. */
  readonly occupantName?: (id: number) => string | null;
  /**
   * Ownership rule: when given, the footprint must lie within `radius` tiles
   * (Chebyshev) of a tile occupied by one of the placing player's own
   * buildings. Omitted for predefined starting structures.
   */
  readonly territory?: {
    readonly radius: number;
    readonly isOwnTile: (x: number, y: number) => boolean;
  };
}

/**
 * The single source of truth for "can this building go here?".
 * Pure: reads map + occupancy, never changes them.
 *
 * Universal rules, per tile:
 *   1. inside the map
 *   2. terrain is buildable
 *   3. not occupied by another building
 *   4. not inside an infected spawn region
 * Whole-footprint rules:
 *   5. ownership: within the player's territory (near their own buildings)
 *   6. `requiresNearby` (e.g. a sawmill needs forest around it)
 */
export function validatePlacement(ctx: PlacementContext, defOrId: BuildingDefinition | BuildingDefinition['id'], origin: GridPoint): PlacementResult {
  const def = typeof defOrId === 'string' ? buildingDef(defOrId) : defOrId;
  const { map, occupancy } = ctx;
  const failures: PlacementFailure[] = [];
  const add = (f: PlacementFailure): void => {
    if (!failures.includes(f)) failures.push(f);
  };
  let badTerrain: string | null = null;
  let occupant: string | null = null;

  const tiles: TileCheck[] = footprintTiles(origin, def.footprint).map((t) => {
    let reason: PlacementFailure | null = null;
    if (!map.inBounds(t.x, t.y)) reason = 'out_of_bounds';
    else if (!map.isBuildable(t.x, t.y)) {
      reason = 'terrain';
      badTerrain ??= map.info(t.x, t.y).name;
    } else if (!occupancy.isFree(t.x, t.y)) {
      reason = 'occupied';
      occupant ??= ctx.occupantName?.(occupancy.at(t.x, t.y)) ?? 'another building';
    } else if (map.spawnRegionAt(t.x, t.y)) reason = 'spawn_zone';
    if (reason) add(reason);
    return { x: t.x, y: t.y, ok: reason === null, reason };
  });

  if (ctx.territory && !failures.includes('out_of_bounds') && !inTerritory(origin, def, ctx.territory.radius, ctx.territory.isOwnTile)) {
    add('outside_territory');
  }

  let nearbyCount: number | null = null;
  const rule = def.requiresNearby;
  if (rule && !failures.includes('out_of_bounds')) {
    nearbyCount = countNearby(map, origin, def, rule.terrain, rule.radius);
    if (nearbyCount < rule.minTiles) add('requires_nearby');
  }

  const messages: Record<PlacementFailure, () => string> = {
    out_of_bounds: () => 'Outside the map.',
    terrain: () => `Can't build on ${badTerrain ?? 'this terrain'}.`,
    occupied: () => `Space is taken by ${occupant ?? 'another building'}.`,
    spawn_zone: () => 'Too close to an infected spawn zone.',
    outside_territory: () => `Outside your territory — build within ${ctx.territory?.radius ?? 0} tiles of your own buildings.`,
    insufficient_resources: () => 'Not enough resources.',
    units_in_way: () => 'Units are standing there — move them first.',
    requires_nearby: () => `${def.name} needs at least ${rule!.minTiles} ${rule!.label} within ${rule!.radius} tiles (found ${nearbyCount ?? 0}).`,
  };

  return {
    ok: failures.length === 0,
    definition: def,
    origin: { x: origin.x, y: origin.y },
    tiles,
    failures,
    message: failures.map((f) => messages[f]()).join(' '),
  };
}

function countNearby(map: WorldMap, origin: GridPoint, def: BuildingDefinition, terrain: number, radius: number): number {
  let n = 0;
  const fp = def.footprint;
  for (let y = origin.y - radius; y < origin.y + fp.height + radius; y++) {
    for (let x = origin.x - radius; x < origin.x + fp.width + radius; x++) {
      if (!map.inBounds(x, y)) continue;
      const d = distanceToFootprint({ x, y }, origin, fp);
      if (d === 0 || d > radius) continue;
      if (map.getTerrain(x, y) === terrain) n++;
    }
  }
  return n;
}

function inTerritory(origin: GridPoint, def: BuildingDefinition, radius: number, isOwnTile: (x: number, y: number) => boolean): boolean {
  const fp = def.footprint;
  for (let y = origin.y - radius; y < origin.y + fp.height + radius; y++) {
    for (let x = origin.x - radius; x < origin.x + fp.width + radius; x++) {
      if (isOwnTile(x, y)) return true;
    }
  }
  return false;
}
